import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Alert } from '@alwindows/shared'
import { useCart } from '@alwindows/shared/cart'
import { useCatalog } from '../catalog/useCatalog'
import { ProductCard, buildColourLabels } from '../catalog/ProductCard'
import { CatalogFilters, type CatalogFilterState } from '../catalog/CatalogFilters'
import type { Product } from '../catalog/types'

export function Landing() {
  const { products, prodFams, prodFilters, colours, loading, error } = useCatalog()
  const { count: cartCount } = useCart()
  const [filters, setFilters] = useState<CatalogFilterState>({
    search: '',
    prodFilters: new Set(),
    prodFams: new Set(),
  })

  const visible = useMemo(() => applyFilters(products, filters), [products, filters])
  const colourLabels = useMemo(() => buildColourLabels(colours), [colours])

  return (
    <div className="al-landing">
      {error ? <Alert variant="error" title="Couldn't load catalog">{error}</Alert> : null}

      <div className="al-landing__layout">
        <div className="al-landing__sidebar">
          <Link
            to="/cart"
            className="al-landing__cart-pill"
            aria-label={`Cart: ${cartCount} item${cartCount === 1 ? '' : 's'}`}
          >
            {/* Artwork mirrors cart-icon/src/cart.svg — keep both in sync. */}
            <svg
              className="al-landing__cart-icon"
              width="22"
              height="19"
              viewBox="0 0 24 21"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M0.787502 3.69072e-07C0.352564 3.69072e-07 0 0.352578 0 0.787502C0 1.22243 0.352564 1.575 0.787502 1.575H3.29766L6.0047 14.3473C6.1118 14.8281 6.42691 15.2283 6.82502 15.225H19.95C20.3661 15.2308 20.7488 14.8536 20.7488 14.4375C20.7488 14.0215 20.3661 13.6442 19.95 13.65H7.46486L7.12852 12.075H21C21.3526 12.0724 21.6858 11.8038 21.7629 11.4598L23.6004 3.58477C23.7028 3.12774 23.3059 2.62836 22.8376 2.62501H5.13517L4.7086 0.623439C4.63408 0.273762 4.29503 -0.000367132 3.93751 3.69072e-07H0.787502ZM5.46329 4.20001H10.2375V6.56251H5.9637L5.46329 4.20001ZM11.8125 4.20001H15.4875V6.56251H11.8125V4.20001ZM17.0625 4.20001H21.845L21.2954 6.56251H17.0625V4.20001ZM6.30001 8.13752H10.2375V10.5H6.80039L6.30001 8.13752ZM11.8125 8.13752H15.4875V10.5H11.8125V8.13752ZM17.0625 8.13752H20.9262L20.3766 10.5H17.0625V8.13752ZM9.45002 15.75C8.0096 15.75 6.82502 16.9346 6.82502 18.3751C6.82502 19.8155 8.0096 21.0001 9.45002 21.0001C10.8904 21.0001 12.075 19.8155 12.075 18.3751C12.075 16.9346 10.8904 15.75 9.45002 15.75ZM17.325 15.75C15.8846 15.75 14.7 16.9346 14.7 18.3751C14.7 19.8155 15.8846 21.0001 17.325 21.0001C18.7655 21.0001 19.95 19.8155 19.95 18.3751C19.95 16.9346 18.7655 15.75 17.325 15.75ZM9.45002 17.3251C10.0393 17.3251 10.5 17.7858 10.5 18.3751C10.5 18.9643 10.0393 19.4251 9.45002 19.4251C8.86084 19.4251 8.40002 18.9643 8.40002 18.3751C8.40002 17.7858 8.86084 17.3251 9.45002 17.3251ZM17.325 17.3251C17.9143 17.3251 18.375 17.7858 18.375 18.3751C18.375 18.9643 17.9143 19.4251 17.325 19.4251C16.7359 19.4251 16.275 18.9643 16.275 18.3751C16.275 17.7858 16.7359 17.3251 17.325 17.3251Z" />
            </svg>
            <span className="al-landing__cart-label">Cart</span>
            {cartCount > 0 ? (
              <span className="al-landing__cart-count">{cartCount}</span>
            ) : null}
            <svg
              className="al-landing__cart-arrow"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M9 6l6 6-6 6" />
            </svg>
          </Link>
          <CatalogFilters
            state={filters}
            onChange={setFilters}
            prodFilterOptions={prodFilters}
            prodFamOptions={prodFams}
          />
        </div>
        <section className="al-landing__grid" aria-busy={loading}>
          {loading ? (
            <>
              <span className="al-sr-only" role="status" aria-live="polite">Loading catalog…</span>
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="al-landing__skeleton" aria-hidden="true">
                  <div className="al-landing__skeleton-media" />
                  <div className="al-landing__skeleton-body">
                    <div className="al-landing__skeleton-line al-landing__skeleton-line--title" />
                    <div className="al-landing__skeleton-line al-landing__skeleton-line--sub" />
                    <div className="al-landing__skeleton-row">
                      <div className="al-landing__skeleton-line al-landing__skeleton-line--price" />
                      <div className="al-landing__skeleton-line al-landing__skeleton-line--qty" />
                    </div>
                  </div>
                </div>
              ))}
            </>
          ) : visible.length === 0 ? (
            <p className="al-landing__status">No matching products.</p>
          ) : (
            visible.map((p) => (
              <ProductCard key={p.refMaterial} product={p} colourLabels={colourLabels} />
            ))
          )}
        </section>
      </div>
    </div>
  )
}

function applyFilters(products: Product[], f: CatalogFilterState): Product[] {
  const term = f.search.trim().toUpperCase()
  return products.filter((p) => {
    const head = p.variants[0]
    if (!head) return false
    if (term && !head.Desc.toUpperCase().includes(term)) return false
    if (f.prodFilters.size > 0 && !f.prodFilters.has(head.ProdFilter)) return false
    // Paints (ProdFilter === "PS") never have a meaningful ProdFam; show them
    // whenever any prodFams filter is on, so the user doesn't accidentally hide paints.
    if (f.prodFams.size > 0 && head.ProdFilter !== 'PS' && !f.prodFams.has(head.ProdFam)) return false
    return true
  })
}
